import { useSession } from '@batiflow/client-core/react';
import { EmptyState, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  __css,
  tokens,
} from '@fluentui/react-components';
import { History24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { canManageSpace, type Space } from '../model';
import { usePartners, useSpaceAudit } from './api';
import { auditActionKey, dateTimeText } from './model';
import { LoadState, SharingSection } from './sharing-ui';

const useStyles = __css({"more":{"mc9l5x":"f22iagw","Brf1p80":"f4d9j23"},"wrap":{"B68tc82":"f1oy3dpc"},"muted":{"sj55zd":"fkfq4zb"}});

/**
 * The Audit tab: who did what in the space and when, in plain language. The host sees every
 * event; a partner admin only those that concern its organisation (the server filters). Older
 * events load page by page.
 */
export function SpaceAuditTab({ space }: { space: Space }) {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const locale = useLocale();
  const audit = useSpaceAudit(space.id);
  const memberships = useSession((state) => state.me?.memberships);
  // The names of the organisations an event concerns: the partners (for the host's managers) and
  // the reader's own organisations.
  const partners = usePartners(space.id, canManageSpace(space));
  const names = new Map<string, string>();
  for (const membership of memberships ?? []) {
    names.set(membership.organizationId, membership.organizationName);
  }
  for (const partner of partners.data?.items ?? []) {
    names.set(partner.organizationId, partner.organizationName);
  }

  if (audit.isError && !audit.data) {
    return (
      <LoadState
        title={t('sharing.audit.loadFailedTitle')}
        body={t('sharing.loadFailedBody')}
        onRetry={() => {
          void audit.refetch();
        }}
      />
    );
  }
  if (!audit.data) return <SkeletonList count={6} />;

  const events = audit.data.pages.flatMap((page) => page.items);
  return (
    <SharingSection
      id="audit-list"
      title={t('sharing.audit.heading')}
      description={t('sharing.audit.description')}
    >
      {events.length === 0 ? (
        <EmptyState
          headingLevel={3}
          illustration={<History24Regular />}
          title={t('sharing.audit.emptyTitle')}
          description={t('sharing.audit.emptyBody')}
        />
      ) : (
        <div
          className={styles.wrap}
          // A group, not a second region: the section around it is already the landmark.
          role="group"
          aria-label={t('sharing.audit.heading')}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be focusable
          tabIndex={0}
        >
          <Table aria-label={t('sharing.audit.heading')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('sharing.audit.columns.date')}</TableHeaderCell>
                <TableHeaderCell>{t('sharing.audit.columns.actor')}</TableHeaderCell>
                <TableHeaderCell>{t('sharing.audit.columns.action')}</TableHeaderCell>
                <TableHeaderCell>{t('sharing.audit.columns.organisation')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => {
                const key = auditActionKey(event.action);
                const organisation = event.partnerOrganizationId
                  ? (names.get(event.partnerOrganizationId) ?? t('sharing.audit.otherOrganisation'))
                  : null;
                return (
                  <TableRow key={event.id}>
                    <TableCell>
                      <TableCellLayout>{dateTimeText(event.occurredAt, locale)}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {event.actorName ? (
                          <bdi>{event.actorName}</bdi>
                        ) : (
                          <span className={styles.muted}>{t('sharing.audit.system')}</span>
                        )}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {key
                          ? t(`sharing.audit.actions.${key}`)
                          : t('sharing.audit.actions.other', { action: isolate(event.action) })}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {organisation ? (
                          <bdi>{organisation}</bdi>
                        ) : (
                          <span className={styles.muted}>{t('sharing.audit.none')}</span>
                        )}
                      </TableCellLayout>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {audit.hasNextPage ? (
        <div className={styles.more}>
          <Button
            appearance="secondary"
            disabledFocusable={audit.isFetchingNextPage}
            onClick={() => {
              void audit.fetchNextPage();
            }}
          >
            {audit.isFetchingNextPage ? t('sharing.audit.loading') : t('sharing.audit.more')}
          </Button>
        </div>
      ) : null}
    </SharingSection>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/audit-tab.griffel.css";